/**
 * BuddyGroupMemberPartnerSelect — dropdown for picking a team-roping partner
 * from the current user's buddy group members.
 *
 * Rendered side-by-side with the generic PartnerSearchCombobox during
 * registration when the user belongs to a buddy group for the rodeo, so
 * they don't have to type-search a teammate whose name we already know.
 *
 * Filters out:
 *   - the current user (can't partner with self)
 *   - members with a pending invitation (not yet accepted into the group)
 *   - members already registered for THIS event (would create a conflict)
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { BuddyGroupMemberView } from '@/types/buddy-groups';

interface BuddyGroupMemberPartnerSelectProps {
  members: BuddyGroupMemberView[];
  /** Current user's user_profile_id — filtered out of the picker */
  currentUserProfileId?: string | null;
  /** Member user_profile_ids already registered for this event — filtered out */
  excludeUserProfileIds?: Set<string>;
  /** Currently-selected partner user_profile_id (form state) */
  value: string;
  /** Called when the user picks a member or clears the selection */
  onChange: (member: BuddyGroupMemberView | null) => void;
  /** Position the partner will rope as ('Header' or 'Heeler') */
  partnerPosition: string;
}

export function BuddyGroupMemberPartnerSelect({
  members,
  currentUserProfileId,
  excludeUserProfileIds,
  value,
  onChange,
  partnerPosition,
}: BuddyGroupMemberPartnerSelectProps) {
  const eligible = members.filter((m) => {
    if (m.invitation_status === 'pending') return false;
    if (currentUserProfileId && m.user_profile_id === currentUserProfileId) {
      return false;
    }
    if (excludeUserProfileIds && excludeUserProfileIds.has(m.user_profile_id)) {
      return false;
    }
    return true;
  });

  if (eligible.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No buddy group teammates are available to partner with for this event.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <Select
        value={value || ''}
        onValueChange={(v) => {
          const picked = eligible.find((m) => m.user_profile_id === v) ?? null;
          onChange(picked);
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Choose a buddy group member..." />
        </SelectTrigger>
        <SelectContent>
          {eligible.map((m) => {
            const label = m.display_name ?? 'Unknown';
            const number = m.member_number ? ` · #${m.member_number}` : '';
            return (
              <SelectItem
                key={m.user_profile_id}
                value={m.user_profile_id}
              >
                {label}
                {number}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        Pick a teammate — they will rope as <strong>{partnerPosition}</strong>.
      </p>
    </div>
  );
}
